import { createContext, useContext, type ReactNode } from 'react'
import { useIncomingScreenTelemetry } from '../features/calls/hooks/use-incoming-screen-telemetry'
import { useUnlockAudioOnGesture } from '../hooks/use-call-ringtone'
import { useVoiceCallController } from '../hooks/use-voice-call-controller'
import { useVoiceCallRingtone } from '../hooks/use-voice-call-ringtone'
import { useWebCallAlert } from '../hooks/use-web-call-alert'
import { useSelfChatUserId } from '../hooks/use-self-chat-user-id'
import { useAuth } from './auth-context'

type VoiceCallContextValue = ReturnType<typeof useVoiceCallController>

const VoiceCallContext = createContext<VoiceCallContextValue | null>(null)

export function VoiceCallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const authUserId = user?.userId
  const chatUserId = useSelfChatUserId(authUserId)

  useUnlockAudioOnGesture()
  useVoiceCallRingtone()
  useIncomingScreenTelemetry()
  useWebCallAlert(chatUserId ?? authUserId)
  // The controller already returns a referentially-stable (memoized) value.
  const value = useVoiceCallController(chatUserId ?? authUserId)

  return <VoiceCallContext.Provider value={value}>{children}</VoiceCallContext.Provider>
}

export function useVoiceCall(): VoiceCallContextValue {
  const ctx = useContext(VoiceCallContext)
  if (!ctx) {
    throw new Error('useVoiceCall must be used within VoiceCallProvider')
  }
  return ctx
}
